import { Component, OnInit, Input, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { RangePickerComponent } from 'src/app/pages/theme/range-picker/range-picker.component';
import { Locations } from 'src/app/_global/locations';
import { StoresManager } from 'src/app/_global/stores-manager';
import { AdvancedReorderRuleModel } from 'src/app/_models/advanced-reorder-rule.model';
import { AdvancedReorderRulesService } from 'src/app/_services/advanced-reorder-rules.service';

@Component({
  selector: 'edit-advanced-reorder-rule',
  templateUrl: './edit-advanced-reorder-rule.component.html',
  styleUrls: ['./edit-advanced-reorder-rule.component.scss']
})
export class EditAdvancedReorderRuleComponent implements OnInit, AfterViewInit {
  @Input() reorderRule: AdvancedReorderRuleModel;
  @ViewChild('orderFor') orderFor: RangePickerComponent;
  @ViewChild('basedOn') basedOn: RangePickerComponent;
  reorderRuleForm: UntypedFormGroup
  locationsFormGroup: UntypedFormGroup;
  storesFormGroup: UntypedFormGroup;
  overstockedStoresFormGroup: UntypedFormGroup;
  loading: boolean = true;
  sign: string = '+';
  
  constructor(public modal: NgbActiveModal,
    private advReorderRulesService: AdvancedReorderRulesService,
    public locations: Locations,
    public stores: StoresManager,
    private toastr: ToastrService,
    private fb: UntypedFormBuilder) { }

  ngOnInit() {
    this.sign = this.reorderRule.adjustment < 0 ? '-' : '+';
    this.reorderRuleForm = this.fb.group({
      name: [this.reorderRule.name, { validators: [Validators.required] }],
      outOfStock: [this.reorderRule.outOfStock, { validators: [Validators.required] }],
      buyBox: [this.reorderRule.buyBox, { validators: [Validators.required] }],
      minusInventory: [this.reorderRule.minusAllInventory, { validators: [Validators.required] }],
      minusStoreInventory: [this.reorderRule.minusAllStoresInventory, { validators: [Validators.required] }],
      minusOverstockedStoreInventory: [this.reorderRule.minusAllStoresOverstockedInventory, { validators: [Validators.required] }],
      minusOpenPurchases: [this.reorderRule.minusOpenPurchases, { validators: [Validators.required] }],
      adjustment: [Math.abs(this.reorderRule.adjustment) || 0, { validators: [Validators.required] }],
    });
    this.locations.getAll(locations => {
      this.stores.getAll(stores => {
        let allLocationsForm = {};
        for (let location of locations)
          allLocationsForm[location.id] = this.reorderRule.minusLocations?.includes(location.id);
        let allStoresForm = {};
        let allOverstockedStoresForm = {};
        for (let store of stores) {
          allStoresForm[store.id] = this.reorderRule.minusStores?.includes(store.id);
          allOverstockedStoresForm[store.id] = this.reorderRule.minusOverstockedStores?.includes(store.id);
        }
        this.locationsFormGroup = this.fb.group(allLocationsForm);
        this.locationsFormGroup.valueChanges.subscribe(() => this.locationSelected());
        this.storesFormGroup = this.fb.group(allStoresForm);
        this.storesFormGroup.valueChanges.subscribe(() => this.storeSelected());
        this.overstockedStoresFormGroup = this.fb.group(allOverstockedStoresForm);
        this.overstockedStoresFormGroup.valueChanges.subscribe(() => this.overstockStoreSelected());
        if (this.reorderRule.minusAllInventory)
          this.setAllLocations(this.reorderRule.minusAllInventory);
        else if (this.reorderRule.minusLocations?.length)
          this.reorderRuleForm.controls.minusInventory.setValue(true, { emitEvent: false });
        this.reorderRuleForm.controls.minusInventory.valueChanges.subscribe(value => this.setAllLocations(value));
        if (this.reorderRule.minusAllStoresInventory)
          this.setAllStores(this.reorderRule.minusAllStoresInventory);
        else if (this.reorderRule.minusStores?.length)
          this.reorderRuleForm.controls.minusStoreInventory.setValue(true, { emitEvent: false });
        this.reorderRuleForm.controls.minusStoreInventory.valueChanges.subscribe(value => this.setAllStores(value));
        if (this.reorderRule.minusAllStoresOverstockedInventory)
          this.setAllOverstockedStores(this.reorderRule.minusAllStoresOverstockedInventory);
        else if (this.reorderRule.minusOverstockedStores?.length)
          this.reorderRuleForm.controls.minusOverstockedStoreInventory.setValue(true, { emitEvent: false });
        this.reorderRuleForm.controls.minusOverstockedStoreInventory.valueChanges.subscribe(value => this.setAllOverstockedStores(value));
      })
    });
  }

  ngAfterViewInit() {
    this.basedOn.selectedDays = this.reorderRule.basedOn;
    if (this.reorderRule.basedOnMonths) {
      this.basedOn.hasCustom = true;
      for(let month of this.reorderRule.basedOnMonths.replace(/{|}/g, '').split(',')) {
        let date = moment(month);
        this.basedOn.selectMonth(date.month(), date.year() - 2000);
      }      
    }
    this.orderFor.selectedDays = this.reorderRule.orderFor;
    if (this.reorderRule.orderForMonths) {
      this.orderFor.hasCustom = true;
      for(let month of this.reorderRule.orderForMonths.replace(/{|}/g, '').split(',')) {
        let date = moment(month);
        this.orderFor.selectMonth(date.month(), date.year() - 2000);
      }
    }
  }

  setAllLocations(value: boolean) {
    if (this.locationsFormGroup) {
      for (let locationId of Object.keys(this.locationsFormGroup.value)) {
        if (this.locationsFormGroup.value[locationId] != value)
          this.locationsFormGroup.controls[locationId].setValue(value, { emitEvent: false });
      }
    }
    this.reorderRule.minusAllInventory = value;
  }

  setAllStores(value: boolean) {
    if (this.storesFormGroup) {
      for (let storeId of Object.keys(this.storesFormGroup.value)) {
        if (this.storesFormGroup.value[storeId] != value)
          this.storesFormGroup.controls[storeId].setValue(value, { emitEvent: false });
      }
    }
    this.reorderRule.minusAllStoresInventory = value;
    if (!value)
      this.reorderRuleForm.controls.minusOverstockedStoreInventory.setValue(value);
  }

  setAllOverstockedStores(value: boolean) {
    if (this.overstockedStoresFormGroup) {
      for (let storeId of Object.keys(this.overstockedStoresFormGroup.value)) {
        if (this.overstockedStoresFormGroup.value[storeId] != value)
          this.overstockedStoresFormGroup.controls[storeId].setValue(value, { emitEvent: false });
      }
    }
    this.reorderRule.minusAllStoresOverstockedInventory = value;
    if (value)
      this.reorderRuleForm.controls.minusStoreInventory.setValue(value);
  }

  locationSelected() {
    let minusInventory = false;
    for (let locationId of Object.keys(this.locationsFormGroup.value))
      if (this.locationsFormGroup.value[locationId])
        minusInventory = true;
      else
        this.reorderRule.minusAllInventory = false;
    if (this.reorderRuleForm.value.minusInventory != minusInventory)
      this.reorderRuleForm.controls.minusInventory.setValue(minusInventory, { emitEvent: false });
  }
  
  storeSelected() {
    let minusStoreInventory = false;
    for (let storeId of Object.keys(this.storesFormGroup.value))
      if (this.storesFormGroup.value[storeId])
        minusStoreInventory = true;
      else {
        this.reorderRule.minusAllStoresInventory = false;
        if (this.overstockedStoresFormGroup.value[storeId]) {
          this.overstockedStoresFormGroup.controls[storeId].setValue(false);
          this.reorderRule.minusAllStoresOverstockedInventory = false;
        }
      }
    if (this.reorderRuleForm.value.minusStoreInventory != minusStoreInventory)
      this.reorderRuleForm.controls.minusStoreInventory.setValue(minusStoreInventory, { emitEvent: false });
  }
 
  overstockStoreSelected() {
    let minusOverstockedStoreInventory = false;
    for (let storeId of Object.keys(this.overstockedStoresFormGroup.value))
      if (this.overstockedStoresFormGroup.value[storeId]) {
        minusOverstockedStoreInventory = true;
        if (!this.storesFormGroup.value[storeId])
          this.storesFormGroup.controls[storeId].setValue(true);
      }
      else
        this.reorderRule.minusAllStoresOverstockedInventory = false;
    if (this.reorderRuleForm.value.minusOverstockedStoreInventory != minusOverstockedStoreInventory)
      this.reorderRuleForm.controls.minusOverstockedStoreInventory.setValue(minusOverstockedStoreInventory, { emitEvent: false });
  }

  get overstockedDisabled() {
    return Object.keys(this.storesFormGroup?.value || {}).every(storeId => this.storesFormGroup.value[storeId])
      || this.reorderRuleForm.value.minusOverstockedStoreInventory ? null : true;
  }

  saveRule() {
    this.loading = true;
    this.reorderRule.basedOn = this.basedOn.selectedDays;
    this.reorderRule.basedOnMonths = this.basedOn.hasCustom ? `{${this.basedOn.getSelectedMonths().join(',')}}` : null;
    this.reorderRule.orderFor = this.orderFor.selectedDays;
    this.reorderRule.orderForMonths = this.orderFor.hasCustom ? `{${this.orderFor.getSelectedMonths().join(',')}}` : null;
    this.reorderRule.minusLocations = Object.keys(this.locationsFormGroup.value).filter(id => this.locationsFormGroup.value[id] == true).map(id => Number(id));
    this.reorderRule.minusStores = Object.keys(this.storesFormGroup.value).filter(id => this.storesFormGroup.value[id] == true).map(id => Number(id));
    this.reorderRule.minusOverstockedStores = Object.keys(this.overstockedStoresFormGroup.value).filter(id => this.overstockedStoresFormGroup.value[id] == true).map(id => Number(id));
    const rule = {
      ...this.reorderRule,
      ...this.reorderRuleForm.value,
      adjustment: this.reorderRuleForm.value.adjustment * (this.sign == '-' ? -1 : 1),
      id: this.reorderRule.id
    };
    if (this.reorderRule.id) {
      this.advReorderRulesService.update(rule).subscribe(res => {
        this.loading = false;
        this.toastr.success(`Reorder Rule ${this.reorderRule.name} updated successfully`);
        this.modal.close('updated');
      }, () => this.loading = false);
    } else {
      this.advReorderRulesService.create(rule).subscribe(
        res => {
          this.reorderRule.id = res.id;
          this.loading = false;
          this.toastr.success(`New Reorder Rule ${this.reorderRule.name} created successfully`);
          this.modal.close('updated');
        }, err => this.loading = false);
    }
  }
}

